import { useEffect, useState } from 'react';

import { ImportProjectDialog } from '@/components/projects/ImportProjectDialog';
import { Button } from '@/components/ui/button';
import { thumbnailFor } from '@/lib/thumbnail';
import { cn } from '@/lib/utils';
import { useApp } from '@/store/useApp';

const COLLAPSED_KEY = 'cdc:projectRailCollapsed';

function readCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(COLLAPSED_KEY) === '1';
}

type RailTab = 'projects' | 'saved';

export function ProjectRail() {
  const projects = useApp((s) => s.projects);
  const activeId = useApp((s) => s.activeProjectId);
  const create = useApp((s) => s.createProject);
  const setActive = useApp((s) => s.setActiveProject);
  const rename = useApp((s) => s.renameProject);
  const remove = useApp((s) => s.deleteProject);
  const savedFiles = useApp((s) => s.savedFiles);
  const deleteSavedFile = useApp((s) => s.deleteSavedFile);
  const newProjectFromSavedFile = useApp((s) => s.newProjectFromSavedFile);

  const [tab, setTab] = useState<RailTab>('projects');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);

  useEffect(() => {
    window.localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  return (
    <aside
      aria-label="Projects"
      className={cn(
        'relative flex h-full shrink-0 flex-col gap-2 border-r border-neutral-200 bg-neutral-50/40 transition-[width] duration-150 dark:border-neutral-800 dark:bg-neutral-950/40',
        collapsed ? 'w-12 p-2' : 'w-56 p-3',
      )}
    >
      {collapsed ? (
        <CollapsedList
          projects={projects}
          activeId={activeId}
          onPickProject={(id) => void setActive(id)}
          onCreate={() => void create()}
          onImport={() => setImportOpen(true)}
        />
      ) : (
        <>
          {/* Tab switcher */}
          <div className="flex rounded-md border border-neutral-200 p-0.5 dark:border-neutral-800">
            <button
              type="button"
              onClick={() => setTab('projects')}
              className={cn(
                'flex-1 rounded py-0.5 text-[11px] font-medium transition-colors',
                tab === 'projects'
                  ? 'bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900'
                  : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300',
              )}
            >
              Projects
            </button>
            <button
              type="button"
              onClick={() => setTab('saved')}
              className={cn(
                'flex-1 rounded py-0.5 text-[11px] font-medium transition-colors',
                tab === 'saved'
                  ? 'bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900'
                  : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300',
              )}
            >
              Saved Files
            </button>
          </div>

          {tab === 'projects' ? (
            <>
              <div className="flex items-center justify-between gap-1">
                <div className="flex items-center gap-1 ml-auto">
                  <Button size="sm" variant="ghost" onClick={() => setImportOpen(true)} title="Import HTML/CSS">
                    Import
                  </Button>
                  <Button size="sm" onClick={() => void create()}>
                    New
                  </Button>
                </div>
              </div>
              <ul className="flex flex-1 flex-col gap-1 overflow-y-auto">
                {projects.length === 0 && (
                  <li className="rounded-md p-2 text-xs text-neutral-400">
                    No projects yet. Click <strong>New</strong> to start.
                  </li>
                )}
                {projects.map((p) => {
                  const isActive = p.id === activeId;
                  const isRenaming = renamingId === p.id;
                  return (
                    <li key={p.id}>
                      <div
                        className={cn(
                          'group flex items-center gap-1 rounded-md p-2 text-sm transition-colors',
                          isActive
                            ? 'bg-neutral-200/70 dark:bg-neutral-800'
                            : 'hover:bg-neutral-100 dark:hover:bg-neutral-900',
                        )}
                      >
                        <img
                          src={p.thumbnailDataUrl ?? thumbnailFor(p)}
                          alt=""
                          aria-hidden="true"
                          className="h-7 w-9 shrink-0 rounded border border-neutral-200 dark:border-neutral-700"
                        />
                        {isRenaming ? (
                          <input
                            autoFocus
                            value={draftName}
                            onChange={(e) => setDraftName(e.target.value)}
                            onBlur={() => {
                              void rename(p.id, draftName);
                              setRenamingId(null);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                void rename(p.id, draftName);
                                setRenamingId(null);
                              } else if (e.key === 'Escape') {
                                setRenamingId(null);
                              }
                            }}
                            className="w-full rounded border border-neutral-300 bg-white px-1 text-sm outline-none dark:border-neutral-700 dark:bg-neutral-900"
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => void setActive(p.id)}
                            onDoubleClick={() => {
                              setRenamingId(p.id);
                              setDraftName(p.name);
                            }}
                            className="flex-1 truncate text-left"
                            title={`${p.name} (double-click to rename)`}
                          >
                            {p.name || 'Untitled'}
                          </button>
                        )}
                        <button
                          type="button"
                          className="invisible rounded p-1 text-neutral-400 hover:bg-neutral-200 hover:text-red-600 group-hover:visible dark:hover:bg-neutral-700"
                          onClick={() => {
                            if (window.confirm(`Delete "${p.name}"? This cannot be undone.`)) {
                              void remove(p.id);
                            }
                          }}
                          title="Delete project"
                          aria-label={`Delete ${p.name}`}
                        >
                          ×
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <SavedFilesTab
              savedFiles={savedFiles}
              onDelete={(id) => {
                if (window.confirm('Delete this saved file? This cannot be undone.')) {
                  void deleteSavedFile(id);
                }
              }}
              onNewProject={(id) => void newProjectFromSavedFile(id)}
            />
          )}
        </>
      )}

      <CollapseToggle collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} />
      <ImportProjectDialog open={importOpen} onOpenChange={setImportOpen} />
    </aside>
  );
}

function SavedFilesTab({
  savedFiles,
  onDelete,
  onNewProject,
}: {
  savedFiles: ReturnType<typeof useApp.getState>['savedFiles'];
  onDelete: (id: string) => void;
  onNewProject: (id: string) => void;
}) {
  if (savedFiles.length === 0) {
    return (
      <p className="rounded-md p-2 text-xs text-neutral-400">
        No saved files yet. Click <strong>Save Design</strong> in the toolbar to save a snapshot.
      </p>
    );
  }
  return (
    <ul className="flex flex-1 flex-col gap-1 overflow-y-auto">
      {savedFiles.map((f) => (
        <li key={f.id}>
          <div className="group flex flex-col gap-1 rounded-md p-2 hover:bg-neutral-100 dark:hover:bg-neutral-900">
            <div className="flex items-center gap-1">
              <span className="flex-1 truncate text-sm font-medium" title={f.name}>
                {f.name}
              </span>
              <button
                type="button"
                className="invisible rounded p-1 text-neutral-400 hover:bg-neutral-200 hover:text-red-600 group-hover:visible dark:hover:bg-neutral-700"
                onClick={() => onDelete(f.id)}
                title="Delete saved file"
                aria-label={`Delete ${f.name}`}
              >
                ×
              </button>
            </div>
            <p className="text-[10px] text-neutral-400">
              {new Date(f.createdAt).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </p>
            <button
              type="button"
              onClick={() => onNewProject(f.id)}
              className="mt-0.5 rounded border border-neutral-300 bg-white px-2 py-0.5 text-[11px] text-neutral-600 hover:bg-neutral-50 hover:text-neutral-800 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
            >
              Open as new project
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}

function CollapseToggle({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      title={collapsed ? 'Expand projects' : 'Collapse projects'}
      aria-label={collapsed ? 'Expand projects rail' : 'Collapse projects rail'}
      aria-expanded={!collapsed}
      className={cn(
        'mt-auto flex w-full shrink-0 items-center gap-1.5 rounded border border-neutral-200 bg-white/60 px-2 py-1.5 text-[11px] text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700 dark:border-neutral-800 dark:bg-neutral-950/60 dark:hover:bg-neutral-900 dark:hover:text-neutral-200',
        collapsed && 'justify-center px-0',
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className={cn('h-3.5 w-3.5 transition-transform', collapsed ? '' : 'rotate-180')}
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z"
          clipRule="evenodd"
        />
      </svg>
      {!collapsed && <span>Collapse</span>}
    </button>
  );
}

function CollapsedList({
  projects,
  activeId,
  onPickProject,
  onCreate,
  onImport,
}: {
  projects: ReturnType<typeof useApp.getState>['projects'];
  activeId: string | null;
  onPickProject: (id: string) => void;
  onCreate: () => void;
  onImport: () => void;
}) {
  return (
    <div className="flex flex-1 flex-col items-center gap-2">
      <ul className="flex flex-1 w-full flex-col items-center gap-1 overflow-y-auto">
        {projects.map((p) => {
          const isActive = p.id === activeId;
          return (
            <li key={p.id} className="w-full">
              <button
                type="button"
                onClick={() => onPickProject(p.id)}
                title={p.name || 'Untitled'}
                aria-label={`Open project ${p.name || 'Untitled'}`}
                className={cn(
                  'flex w-full items-center justify-center rounded p-1 transition-colors',
                  isActive
                    ? 'bg-neutral-200/70 dark:bg-neutral-800'
                    : 'hover:bg-neutral-100 dark:hover:bg-neutral-900',
                )}
              >
                <img
                  src={p.thumbnailDataUrl ?? thumbnailFor(p)}
                  alt=""
                  aria-hidden="true"
                  className="h-6 w-8 rounded border border-neutral-200 dark:border-neutral-700"
                />
              </button>
            </li>
          );
        })}
      </ul>
      <div className="flex w-full flex-col items-center gap-1 border-t border-neutral-200 pt-2 dark:border-neutral-800">
        <button
          type="button"
          onClick={onImport}
          title="Import HTML/CSS"
          aria-label="Import HTML/CSS"
          className="flex h-7 w-7 items-center justify-center rounded text-neutral-500 hover:bg-neutral-200 dark:hover:bg-neutral-800"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
            <path d="M9.25 13.25a.75.75 0 0 0 1.5 0V4.66l1.95 2.1a.75.75 0 1 0 1.1-1.02l-3.25-3.5a.75.75 0 0 0-1.1 0L6.2 5.74a.75.75 0 1 0 1.1 1.02L9.25 4.66v8.59Z" />
            <path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z" />
          </svg>
        </button>
        <button
          type="button"
          onClick={onCreate}
          title="New project"
          aria-label="New project"
          className="flex h-7 w-7 items-center justify-center rounded bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-neutral-200"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
            <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
          </svg>
        </button>
      </div>
    </div>
  );
}
